import { useEffect } from 'react';
import { useBushingStore } from '@/stores/useBushingStore';
import { useInputStore } from '@/stores/useInputStore';
import { isByd } from '@/constants/bushingTypes';
import type { ParamRange } from '@/api/trainingDataApi';
import type { BushingType } from '@/types/bushing';

/**
 * Compute the maximum Core OD based on bushing type geometry.
 *
 * Formula per bushing type (ported from Python optibush):
 *   A1/C1:         max_od = (1.05 * control_arm_id - 2 * sleeve_thk) - 9
 *   B1:            max_od = (1.05 * control_arm_id - 2 * (sleeve_thk + 1.5)) - 12.5
 *   D1/BYD Voided: max_od = (1.05 * control_arm_id - 2 * sleeve_thk) - 15
 *
 * Constraint: max_od is limited by training data maximum (core_od_hi).
 * Result is rounded to one decimal place.
 */
function computeCoreOdMax(
  controlArmId: number | null,
  bushingType: BushingType,
  sleeveThickness: number,
  coreOdHi: number | null,
): number | null {
  if (controlArmId == null) return null;

  const controlId = Math.abs(controlArmId);

  let offset: number;
  let interSleeveThk: number;

  if (bushingType === 'D1' || bushingType === 'BYD EV Voided') {
    offset = 15.0;
    interSleeveThk = 0.0;
  } else if (bushingType === 'B1') {
    offset = 12.5;
    interSleeveThk = 1.5;
  } else {
    // A1, C1 etc.
    offset = 9.0;
    interSleeveThk = 0.0;
  }

  let calculatedMaxOd = (1.05 * controlId) - 2 * (sleeveThickness + interSleeveThk) - offset;

  if (coreOdHi != null) {
    calculatedMaxOd = Math.min(calculatedMaxOd, coreOdHi);
  }
    return Math.abs(Math.round(calculatedMaxOd));
}

/**
 * useCoreOdMax — Reactive hook that subscribes to controlArmId,
 * sleeveThickness, bushingType, and the training data coreOd range,
 * then automatically updates `packaging.coreOdMax` whenever any
 * dependency changes.
 */
export function useCoreOdMax(coreOdRange?: ParamRange): void {
  const controlArmId = useInputStore((s) => s.packaging.controlArmId);
  const sleeveThickness = useInputStore((s) => s.sleeveThickness);
  const bushingType = useBushingStore((s) => s.bushingType);

  const coreOdHi = coreOdRange?.max ?? null;

  useEffect(() => {
    // BYD mode: coreOdMax is set directly from the preset — do not overwrite it.
    if (isByd(bushingType)) return;

    const coreOdMax = computeCoreOdMax(
      controlArmId,
      bushingType,
      sleeveThickness,
      coreOdHi,
    );

    // Always write the computed value (even null) so that stale values
    // are cleared when dependencies become null.
    const current = useInputStore.getState().packaging.coreOdMax;
    if (coreOdMax !== current) {
      useInputStore.getState().setPackagingField('coreOdMax', coreOdMax);
    }
  }, [controlArmId, bushingType, sleeveThickness, coreOdHi]);
}
